'use client';

import clsx from 'clsx';
import Link from 'next/link';
import React from 'react';
import { UrlObject } from 'url';
import { analytics } from '@/helpers/Analytics';

/**
 * DS `Button` — design_docs/The-Realest-Design-Skill.md §7.1.
 *
 * The doc lists seven rows; they collapse into two orthogonal props rather
 * than seven variant names:
 *
 *   variant x size x iconOnly          -> DS row
 *   primary   default                  -> Primary            56, #1b1917
 *   primary   mobile                   -> Mobile             44, #1b1917
 *   primary   mini                     -> Mini (primary)     33, #1b1917
 *   secondary default                  -> Secondary          56, BG/300
 *   secondary default  iconOnly        -> Secondary Icon     56x56
 *   secondary mini                     -> Mini (secondary)   33, BG/100 fill
 *   tertiary  default                  -> Tertiary text      56, transparent
 *   tertiary  default  iconOnly        -> Tertiary icon      56x56, transparent
 *
 * The board calls the third variant `Tertiary`; the written doc calls the same
 * thing `Ghost`. Following the board.
 *
 * Structure per the doc: full-width buttons are `space-between` — label left,
 * 20px icon right. Icon-only buttons centre. Mini hugs.
 *
 * Buttons are drawn SQUARE, unlike the Cards components which ship 2px — see
 * Q10. No shadows anywhere in this system; separation is borders and
 * background steps only.
 */
export type DSButtonVariant = 'primary' | 'secondary' | 'tertiary';
export type DSButtonSize = 'default' | 'mobile' | 'mini';
/**
 * Buttons are square everywhere EXCEPT three places the board draws as pills:
 * the nav's Get Started, the mobile nav's Get Started, and the banner CTA.
 * That is a scoped, deliberate exception rather than drift, so it is a prop
 * rather than three hand-rolled copies. See Q10.
 */
export type DSButtonShape = 'square' | 'pill';

export type DSButtonProps = {
    children?: React.ReactNode;
    variant?: DSButtonVariant;
    size?: DSButtonSize;
    /** Square icon-only button. Ignored for `mini`. */
    iconOnly?: boolean;
    shape?: DSButtonShape;
    /** 20px trailing icon. Pushed right on full-width buttons. */
    icon?: React.ReactNode;
    fullWidth?: boolean;
    disabled?: boolean;
    /**
     * Swaps the label for a spinner and disables the button. The library draws
     * no loading state (like disabled and focus, see Q15/Q16), so this is
     * inferred: a currentColor ring, which picks up each variant's text colour
     * rather than hardcoding one.
     */
    loading?: boolean;
    href?: string | UrlObject;
    linkTarget?: string;
    type?: 'button' | 'submit' | 'reset';
    onClick?: (e: React.MouseEvent) => void;
    /**
     * What the Click event calls this button. Defaults to its rendered text,
     * which is right for a label like "Place Your Bid" and wrong for an
     * icon-only control — those should name themselves.
     *
     * `false` opts out entirely, for a button whose click is already reported
     * by the flow around it.
     */
    trackAs?: string | false;
    className?: string;
    'aria-label'?: string;
};

/** Height ladder from the doc: 56 / 44 / 33. */
const SIZE: Record<DSButtonSize, string> = {
    default: 'h-[56px] px-[20px] text-h3',
    mobile: 'h-[44px] px-[20px] text-h4',
    mini: 'h-[33px] px-[16px] py-[6px] text-h4'
};

/* Fills read off the master's Button sets — Primary 44:60, Secondary 44:81,
   Ghost 44:92 — one variant per set, each with Default / Hover / Disabled /
   Focused.

   Primary and Secondary swap fills on hover rather than stepping along the
   ramp: Primary/Hover (44:58) is BG/300 on Text/400, exactly Secondary's
   resting state, and Secondary/Hover (44:79) is BG/600 on Text/100, exactly
   Primary's. This used to step Primary to surface-500, which came from the
   written doc's `BG/400` — the doc has that row wrong.

   `hover:` rather than `hover:enabled:` — `:enabled` only matches form
   controls, so every button rendered as a link (href set) had no hover state
   at all. Tailwind orders `disabled:` after `hover:`, and the disabled fills
   below are declared here, so a disabled button still wins. */
const VARIANT: Record<DSButtonVariant, string> = {
    primary: 'bg-surface-600 text-ink-100 hover:bg-surface-300 hover:text-ink-400',
    secondary: 'bg-surface-300 text-ink-400 hover:bg-surface-600 hover:text-ink-100',
    tertiary: 'bg-transparent text-ink-400 hover:bg-surface-300'
};

/* Disabled is drawn (494:799 / 494:891 / 494:999): the filled variants go flat
   BG/300 with BG/400 text, while Ghost keeps its transparent ground and marks
   itself with a 1px BG/300 outline instead. */
const DISABLED: Record<DSButtonVariant, string> = {
    primary: 'disabled:bg-surface-300 disabled:text-surface-400',
    secondary: 'disabled:bg-surface-300 disabled:text-surface-400',
    tertiary: 'disabled:border disabled:border-surface-300 disabled:bg-transparent disabled:text-surface-400'
};

/** Mini inverts the ramp rather than reusing the full-size fills. */
const MINI_VARIANT: Record<DSButtonVariant, string> = {
    primary: 'bg-surface-600 text-ink-100',
    secondary: 'bg-surface-100 text-ink-400',
    tertiary: 'bg-transparent text-ink-400 hover:bg-surface-300'
};

const DSButton = ({
    children,
    variant = 'primary',
    size = 'default',
    shape = 'square',
    iconOnly = false,
    icon,
    fullWidth = false,
    disabled = false,
    loading = false,
    href,
    linkTarget,
    type = 'button',
    onClick,
    trackAs,
    className,
    ...rest
}: DSButtonProps) => {
    const square = iconOnly && size !== 'mini';
    // A loading button must not be actionable.
    const inert = disabled || loading;

    /*
     * Every button reports its click, as the pill this replaced did — that
     * tracking left with it and took ~70 buttons' worth of funnel data with
     * it, silently, because nothing breaks when an event stops arriving.
     *
     * The name comes from the rendered text rather than from `children`: the
     * old one read only string children, so a button wrapping its label in a
     * span reported an empty name. `aria-label` covers the icon-only ones,
     * which have no text at all.
     *
     * Consent is the analytics helper's business, not this component's — it
     * drops the event when analytics consent is absent.
     */
    const handleClick = (e: React.MouseEvent) => {
        if (trackAs !== false) {
            const name = trackAs
                || e.currentTarget.textContent?.trim()
                || rest['aria-label']
                || '';
            analytics.track('Click', {
                name,
                href: typeof href === 'string' ? href : undefined
            });
        }
        onClick?.(e);
    };

    const classes = clsx(
        // Squared per 3160:729 - not the 2px the Cards components use.
        // Explicit rather than inheriting --ds-radius.
        // Press feedback per §'Buttons must feel responsive': a subtle
        // scale on :active is what makes the UI feel like it is listening.
        'inline-flex items-center transition-[transform,background-color,color] duration-press ease-ds-out',
        !inert && 'active:scale-[0.97]',
        shape === 'pill' ? 'rounded-full' : 'rounded-none',
        // Focused is drawn (494:1332): a 4px accent-blue border, inside the
        // 56px box rather than around it — hence the negative offset, so the
        // ring does not shift anything next to it.
        'focus-visible:outline focus-visible:outline-4 focus-visible:-outline-offset-4 focus-visible:outline-accent-blue',
        'disabled:cursor-not-allowed',
        DISABLED[variant],
        size === 'mini' ? MINI_VARIANT[variant] : VARIANT[variant],
        square ? 'size-[56px] shrink-0 justify-center px-0' : SIZE[size],
        // Full-width buttons push the icon to the far edge; everything else
        // centres and hugs.
        loading ? 'justify-center' : (fullWidth && !square ? 'w-full justify-between' : 'justify-center'),
        loading && fullWidth && !square && 'w-full',
        !fullWidth && !square && icon && 'gap-[8px]',
        className
    );

    const body = loading ? (
        <span
            aria-hidden
            className="size-[20px] animate-spin rounded-full border-2 border-current border-t-transparent"
        />
    ) : (
        <>
            {children}
            {icon && !square && <span className="flex size-[20px] shrink-0 items-center justify-center">{icon}</span>}
            {square && icon}
        </>
    );

    if (href && !inert) {
        return (
            <Link
                prefetch={false}
                href={href}
                target={linkTarget}
                className={clsx('no-hover', classes)}
                onClick={handleClick}
                {...rest}
            >
                {body}
            </Link>
        );
    }

    return (
        <button type={type} disabled={inert} aria-busy={loading} onClick={handleClick} className={classes} {...rest}>
            {body}
        </button>
    );
};

export default DSButton;
